import React, { useEffect, useState } from 'react';
import { useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { CheckCircle, Download, Calendar, FileText, User, CreditCard, Phone, Mail, MapPin, Home } from 'lucide-react';
import axiosInstance from '../api/axiosInstance';
import axios from 'axios';
import InactivityTimer from './InactivityTimer';

const ServicePaymentSuccessPage = () => {
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const paymentId = query.get('pid');
    const [orderData, setOrderData] = useState(null);
    const [qrCodeUrl, setQrCodeUrl] = useState(null);
    const [invoiceData, setInvoiceData] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [invoiceInProgress, setInvoiceInProgress] = useState(false);

    const fetchDetails = async () => {
        if (!paymentId) {
            setError('No payment ID provided in URL');
            return;
        }
        try {
            setLoading(true);
            const response = await axiosInstance.get(`/api/patient/get-service-orders/${paymentId}`);
            
            if (!response.data || !response.data.serviceOrders || !response.data.payment) {
                throw new Error('Invalid response structure');
            }
            setOrderData(response.data);

           const patientResponse = await axios.get(`${process.env.REACT_APP_KIOSK_OPS_URL}/patient/registeredPatientDetails?input=${encodeURIComponent(response.data.patientMrno)}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });

            // Only call invoice API once per paymentId, even after reload and in StrictMode
            const invoiceKey = `invoice_called_${paymentId}`;
            const invoiceAlreadyCalled = localStorage.getItem(invoiceKey);
            if (!invoiceAlreadyCalled) {
                // Set flag BEFORE calling API to prevent double call
                localStorage.setItem(invoiceKey, 'pending');
                // Build serviceList as array of { serviceId, qty }
                const serviceList = response.data.serviceOrders.map(service => ({
                    serviceId: service.service_id,
                    quantity: service.quantity || 1
                }));

                const invoiceRequestData = {
                    serviceList: serviceList,
                    consultantId: "4291",
                    patientId: patientResponse.data.data[0]?.patientId || "2853714",
                    departmentId: localStorage.getItem('departmentId') || "1183575",
                    siteId: localStorage.getItem('site_id'),
                    siteCode: localStorage.getItem('site_code'),
                    userId: 2,
                    payment: {
                        paymentId: response.data.payment.razorpayPaymentId,
                        amount: response.data.payment.amount_received / 100,
                        currency: response.data.payment.currency || 'INR',
                        orderId: response.data.payment.razorpayOrderId,
                        status: response.data.payment.status.toUpperCase() || 'SUCCESS',
                    }
                };

                // console.log('Sending invoice request with data:', invoiceRequestData);
                try {
                    const invoiceResponse = await axios.post(
                        `${process.env.REACT_APP_KIOSK_OPS_URL}/invoice/create`,
                        invoiceRequestData,
                        {
                            headers: {
                                Authorization: `Bearer ${localStorage.getItem('token')}`
                            }
                        }
                    );
                    if (invoiceResponse.data && invoiceResponse.data.reportLink) {
                        setQrCodeUrl(invoiceResponse.data.reportLink);
                        setInvoiceData(invoiceResponse.data);
                        localStorage.setItem(invoiceKey, JSON.stringify(invoiceResponse.data));
                    } else {
                        localStorage.removeItem(invoiceKey);
                        throw new Error('No report link received in response');
                    }
                } catch (err) {
                    localStorage.removeItem(invoiceKey);
                    throw err;
                }
            } else if (invoiceAlreadyCalled && invoiceAlreadyCalled !== 'pending') {
                const cachedData = JSON.parse(invoiceAlreadyCalled);
                setQrCodeUrl(cachedData.reportLink);
                setInvoiceData(cachedData);
            }
        } catch (error) {
            console.error("Error:", error.response?.data || error.message);
            setError(`Failed to process request: ${error.response?.data?.message || error.message}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDetails();
    }, [paymentId]);

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center py-8">
                <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <FileText className="w-8 h-8 text-red-600" />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Error</h2>
                    <p className="text-red-600 mb-6">{error}</p>
                    <button
                        onClick={() => window.location.href = '/'}
                        className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        <Home className="w-4 h-4" />
                        Back to Home
                    </button>
                </div>
            </div>
        );
    }

    if (loading || !orderData) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center py-8">
                <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
                    <div className="animate-spin w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full mx-auto mb-4"></div>
                    <p className="text-gray-600">Processing your invoice...</p>
                </div>
            </div>
        );
    }

    const totalAmount = orderData.payment.amount_received / 100;

    return (
        <>
            <InactivityTimer />
            <div className="min-h-screen bg-gray-50 py-8">
                <div className="max-w-5xl mx-auto px-4">
                    {/* Success Header */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 p-6">
                        <div className="flex items-center justify-center mb-4">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                                <CheckCircle className="w-8 h-8 text-green-600" />
                            </div>
                        </div>
                        <h1 className="text-2xl font-bold text-center text-gray-900 mb-2">Payment Successful!</h1>
                        <p className="text-center text-gray-600">Your invoice has been generated successfully</p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Invoice Details */}
                        <div className="lg:col-span-2">
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                                {/* Invoice Header */}
                                <div className="border-b border-gray-200 p-6">
                                    <div className="flex items-start justify-between">
                                        {/* Logo and Hospital Info */}
                                        <div className="flex items-start gap-4">
                                            <div className="w-16 h-16 bg-blue-600 rounded-lg flex items-center justify-center">
                                                <div className="text-white font-bold text-xl">TC</div>
                                            </div>
                                            <div>
                                                <h2 className="text-xl font-bold text-gray-900">KIMS HOSPITAL</h2>
                                                <div className="text-sm text-gray-600 mt-1 space-y-1">
                                                    <div className="flex items-center gap-2">
                                                        <MapPin className="w-3 h-3" />
                                                        <span>Bhubaneswar, Odisha</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Phone className="w-3 h-3" />
                                                        <span>+91 XXX XXX XXXX</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Mail className="w-3 h-3" />
                                                        <span>info@kims.ac.in</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Invoice Info */}
                                        <div className="text-right">
                                            <div className="text-2xl font-bold text-blue-600 mb-2">INVOICE</div>
                                            {invoiceData && (
                                                <div className="space-y-1 text-sm">
                                                    <div className="flex items-center gap-2 justify-end">
                                                        <FileText className="w-4 h-4 text-gray-400" />
                                                        <span className="text-gray-600">Invoice No:</span>
                                                        <span className="font-semibold">{invoiceData.InvoiceNo}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2 justify-end">
                                                        <Calendar className="w-4 h-4 text-gray-400" />
                                                        <span className="text-gray-600">Date:</span>
                                                        <span className="font-semibold">{invoiceData.invoiceDate}</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Patient and Payment Info */}
                                <div className="p-6 border-b border-gray-200">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* Patient Details */}
                                        <div>
                                            <div className="flex items-center gap-2 mb-3">
                                                <User className="w-5 h-5 text-blue-600" />
                                                <h3 className="font-semibold text-gray-900">Patient Details</h3>
                                            </div>
                                            <div className="space-y-2 text-sm">
                                                <div>
                                                    <span className="text-gray-600">Name: </span>
                                                    <span className="font-medium">{orderData.patientName || 'N/A'}</span>
                                                </div>
                                                <div>
                                                    <span className="text-gray-600">MRNO: </span>
                                                    <span className="font-medium">{orderData.patientMrno || 'N/A'}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Payment Details */}
                                        <div>
                                            <div className="flex items-center gap-2 mb-3">
                                                <CreditCard className="w-5 h-5 text-green-600" />
                                                <h3 className="font-semibold text-gray-900">Payment Details</h3>
                                            </div>
                                            <div className="space-y-2 text-sm">
                                                <div>
                                                    <span className="text-gray-600">Payment ID: </span>
                                                    <span className="font-medium text-xs">{orderData.payment.razorpayPaymentId}</span>
                                                </div>
                                                <div>
                                                    <span className="text-gray-600">Order ID: </span>
                                                    <span className="font-medium text-xs">{orderData.payment.razorpayOrderId}</span>
                                                </div>
                                                <div>
                                                    <span className="text-gray-600">Status: </span>
                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                        {orderData.payment.status.toUpperCase()}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span className="text-gray-600">Date: </span>
                                                    <span className="font-medium">{new Date(orderData.payment.createdAt).toLocaleDateString()}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Services Table */}
                                <div className="p-6">
                                    <h3 className="font-semibold text-gray-900 mb-4">Service Details</h3>
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead>
                                                <tr className="border-b-2 border-gray-200">
                                                    <th className="text-left py-3 px-2 font-semibold text-gray-700">Service Name</th>
                                                    <th className="text-center py-3 px-2 font-semibold text-gray-700">Qty</th>
                                                    <th className="text-right py-3 px-2 font-semibold text-gray-700">Price (₹)</th>
                                                    <th className="text-right py-3 px-2 font-semibold text-gray-700">Total (₹)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {orderData.serviceOrders.map((service, index) => (
                                                    <tr key={service.service_id} className="border-b border-gray-100">
                                                        <td className="py-3 px-2 text-gray-900">{service.service_name || 'N/A'}</td>
                                                        <td className="py-3 px-2 text-center text-gray-700">{service.quantity || 0}</td>
                                                        <td className="py-3 px-2 text-right text-gray-700">₹{service.tariff || 0}</td>
                                                        <td className="py-3 px-2 text-right font-medium text-gray-900">₹{(service.tariff * service.quantity) || 0}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr className="border-t-2 border-gray-200">
                                                    <td colSpan="3" className="py-4 px-2 text-right font-semibold text-gray-900 text-lg">Total Amount:</td>
                                                    <td className="py-4 px-2 text-right font-bold text-xl text-blue-600">₹{totalAmount}</td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* QR Code and Actions */}
                        <div className="lg:col-span-1">
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sticky top-8">
                                {qrCodeUrl ? (
                                    <div className="text-center">
                                        <h3 className="font-semibold text-gray-900 mb-4">Download Invoice</h3>
                                        <div className="bg-gray-50 p-4 rounded-lg mb-4 inline-block">
                                            <QRCodeSVG
                                                value={qrCodeUrl}
                                                size={200}
                                                level="H"
                                                includeMargin={true}
                                            />
                                        </div>
                                        <p className="text-sm text-gray-600 mb-4">
                                            Scan this QR code to download your invoice
                                        </p>
                                        <div className="space-y-3">
                                            <a
                                                href={qrCodeUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                                            >
                                                <Download className="w-4 h-4" />
                                                Download PDF
                                            </a>
                                            <button
                                                onClick={() => window.location.href = '/'}
                                                className="w-full inline-flex items-center justify-center gap-2 bg-gray-600 text-white px-4 py-3 rounded-lg hover:bg-gray-700 transition-colors"
                                            >
                                                <Home className="w-4 h-4" />
                                                Back to Home
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center py-8">
                                        <div className="animate-spin w-6 h-6 border-2 border-blue-200 border-t-blue-600 rounded-full mx-auto mb-4"></div>
                                        <p className="text-gray-600">Generating invoice...</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ServicePaymentSuccessPage;